import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../api/client'

interface State<T> {
  data?: T
  error?: ApiError
  loading: boolean
}

/**
 * useAsync runs `fn` on mount and whenever deps change, and hands back a
 * `reload` for refresh-after-write. The request id guard prevents an older,
 * slower call from overwriting a newer result.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): State<T> & { reload: () => void } {
  const [state, setState] = useState<State<T>>({ loading: true })
  const [tick, setTick] = useState(0)
  const callId = useRef(0)
  const fnRef = useRef(fn)
  fnRef.current = fn

  useEffect(() => {
    const id = ++callId.current
    setState((s) => ({ ...s, loading: true }))
    fnRef.current().then(
      (data) => {
        if (callId.current !== id) return
        setState({ data, loading: false })
      },
      (error: unknown) => {
        if (callId.current !== id || (error as Error)?.name === 'AbortError') return
        setState({ data: undefined, error: error instanceof ApiError ? error : new ApiError(0, 'INTERNAL', String(error), [], ''), loading: false })
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { ...state, reload }
}
